import { Editor, Element, Node, Path, Transforms } from 'slate';

import { CustomText } from '../types';
import { parseExpression } from '../utils';

// [PURPOSE]: Checks if a given text string matches the pattern for a filter chip (e.g., "field operator value"), considering variations like operators requiring no value, parenthesized values for 'IN'/'NOT IN', and quote/parenthesis balancing. Returns the parsed components [field, operator, value] if it matches, otherwise null.
export const isChipPattern = (text: string) => {
  // Early return for empty text
  if (!text || typeof text !== 'string' || text.trim() === '') {
    return null;
  }
  const parsed = parseExpression(text);
  const [[prefixOperator, field, operator, value], { complete }] = parsed;

  return complete ? [prefixOperator, field, operator, value] : null;
};

/**
 * Helper functions for node type checking
 */
// [PURPOSE]: Checks if a Slate node is an Element with a specific 'type' property.
export const isNodeType = (node: any, type: string) =>
  Element.isElement(node) && node.type === type;
// [PURPOSE]: Checks if a node is a 'chip' element.
export const _isChip = (node: any) => isNodeType(node, 'chip');
// [PURPOSE]: Checks if a node is a 'simple-chip' element.
export const _isSimpleChip = (node: any) => isNodeType(node, 'simple-chip');
// [PURPOSE]: Checks if a node is either a 'chip' or a 'simple-chip' element.
export const isChipLike = (node: any) => _isChip(node) || _isSimpleChip(node);

export const isGroupOperator = (node: any) => isNodeType(node, 'grouping-operator');

/**
 * Helper to check node ancestry
 */
// [PURPOSE]: Checks if a node at a given path has an ancestor element of a specific 'type'.
export const isDescendantOfType = (editor: any, path: any, type: any) => {
  try {
    let currentPath = path;
    while (currentPath.length > 0) {
      const [node] = Editor.node(editor, currentPath);
      if (Element.isElement(node) && node.type === type) {
        return true;
      }
      currentPath = Path.parent(currentPath);
    }
  } catch {
    // Ignore path errors
  }

  return false;
};

// [PURPOSE]: Creates an object to track normalization counts for different paths to prevent infinite loops during the Slate normalization process.
export const createNormalizationState = () => ({
  MAX_NORMALIZATIONS: 5,
  counts: new Map(),
});

/**
 * Safely select a point after a node
 */
// [PURPOSE]: Attempts to select the point immediately after a given node path. If that fails (e.g., at the end of the document), it inserts a new text node after the parent and selects within that.
export const selectAfterNode = (editor: any, path: any) => {
  try {
    const pointAfter = Editor.after(editor, path);
    if (pointAfter) {
      Transforms.select(editor, pointAfter);

      return true;
    }

    // Fallback: create a new text node at the end of parent
    const parentPath = Path.parent(path);
    const parentChildrenCount = [...Node.children(editor, parentPath)].length;
    const insertPath = [...parentPath, parentChildrenCount];
    Transforms.insertNodes(editor, { text: ' ' } as CustomText, { at: insertPath });
    Transforms.select(editor, { offset: 1, path: insertPath });

    return true;
  } catch {
    // Error handling without console.error
    return false;
  }
};
